import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useIsDemo } from "../api/auth";
import SensitiveToggle, { useSensitiveMask } from "../components/SensitiveToggle";
import { maskName, maskUid } from "../lib/mask";
import { useDashboard } from "../api/dashboard";
import { errorMessage } from "../api/client";
import { StatusBadge } from "../components/StatusBadge";
import { formatHours, formatPct, hoursTick } from "../lib/format";
import type { DashboardSummary, DashboardUserStat } from "../api/types";

const ACCENT = "var(--accent)";
const GRAY = "var(--gray)";
const TOOLTIP_STYLE = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  fontSize: 12,
};
const CURSOR_FILL = { fill: "rgba(14, 140, 90, 0.08)" };

function userLabel(u: { uid: string | null; display_name: string | null }, masked = false): string {
  if (masked) return maskName(u.display_name) ?? (u.uid ? maskUid(u.uid)! : "Unknown");
  return u.display_name ?? (u.uid ? `${u.uid.slice(0, 8)}…` : "Unknown");
}

function weekLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function KpiRow({ dash }: { dash: DashboardSummary }) {
  const t = dash.totals;
  const tiles = [
    { label: "Pending review", value: String(t.pending_review_count), sub: formatHours(t.pending_review_ms) },
    { label: "Approved", value: String(t.approved_count), sub: formatHours(t.approved_ms) },
    { label: "Rejected", value: String(t.rejected_count), sub: formatHours(t.rejected_ms) },
    { label: "Approval rate", value: formatPct(t.approval_rate), sub: `${t.approved_count + t.rejected_count} reviewed` },
    { label: "Recorded time", value: formatHours(t.total_ms), sub: "pending + reviewed" },
    { label: "Videos", value: String(t.total_count), sub: "pending + reviewed" },
  ];
  return (
    <div className="kpi-row">
      {tiles.map((tile) => (
        <div className="kpi-tile" key={tile.label}>
          <span className="kpi-value">{tile.value}</span>
          <span className="kpi-label">{tile.label}</span>
          <span className="kpi-sub">{tile.sub}</span>
        </div>
      ))}
    </div>
  );
}

function UserRankingCard({ users, masked }: { users: DashboardUserStat[]; masked: boolean }) {
  return (
    <section className="card">
      <div className="card-head">
        <h3>Recorded time by user</h3>
      </div>
      {users.length === 0 && <p className="muted small">No recordings yet.</p>}
      {users.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>User</th>
                <th>Videos</th>
                <th>Recorded</th>
                <th>Approved</th>
                <th>Rejected</th>
                <th>Pending</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.uid ?? `unknown-${i}`}>
                  <td className="rank-pos">{i + 1}</td>
                  <td>{userLabel(u, masked)}</td>
                  <td>{u.total_count}</td>
                  <td>{formatHours(u.total_ms)}</td>
                  <td>{formatHours(u.approved_ms)}</td>
                  <td>{formatHours(u.rejected_ms)}</td>
                  <td>{u.pending_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ApprovedHoursChart({ users, masked }: { users: DashboardUserStat[]; masked: boolean }) {
  const data = [...users]
    .filter((u) => u.approved_ms > 0)
    .sort((a, b) => b.approved_ms - a.approved_ms)
    .slice(0, 15)
    .map((u) => ({ name: userLabel(u, masked), approved_ms: u.approved_ms }));
  return (
    <section className="card">
      <div className="card-head">
        <h3>Approved hours by user</h3>
      </div>
      {data.length === 0 && <p className="muted small">No approved recordings yet.</p>}
      {data.length > 0 && (
        <div className="chart-wrap" style={{ height: Math.max(160, 30 * data.length + 40) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ left: 8, right: 24 }}>
              <CartesianGrid stroke="var(--border-soft)" horizontal={false} />
              <XAxis
                type="number"
                tickFormatter={hoursTick}
                tick={{ fill: "var(--muted)", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={120}
                tick={{ fill: "var(--muted)", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                cursor={CURSOR_FILL}
                contentStyle={TOOLTIP_STYLE}
                formatter={(v) => [formatHours(v as number), "Approved"]}
              />
              <Bar dataKey="approved_ms" fill={ACCENT} barSize={14} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}

function WeeklyChart({ dash }: { dash: DashboardSummary }) {
  const data = dash.weekly.map((p) => ({
    week: weekLabel(p.week_start),
    total_ms: p.total_ms,
    approved_ms: p.approved_ms,
  }));
  return (
    <section className="card">
      <div className="card-head">
        <h3>Hours per week</h3>
      </div>
      <div className="chart-wrap">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ left: 8, right: 24, top: 8 }}>
            <CartesianGrid stroke="var(--border-soft)" vertical={false} />
            <XAxis
              dataKey="week"
              tick={{ fill: "var(--muted)", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={hoursTick}
              tick={{ fill: "var(--muted)", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(v, key) => [
                formatHours(v as number),
                key === "total_ms" ? "Recorded" : "Approved",
              ]}
            />
            <Legend
              formatter={(key) => (key === "total_ms" ? "Recorded" : "Approved")}
              wrapperStyle={{ fontSize: 12 }}
            />
            <Line dataKey="total_ms" stroke={GRAY} strokeWidth={2} dot={false} />
            <Line dataKey="approved_ms" stroke={ACCENT} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function StatusBreakdownCard({ dash }: { dash: DashboardSummary }) {
  const rows = dash.status_counts.filter((c) => c.count > 0);
  return (
    <section className="card">
      <div className="card-head">
        <h3>Pipeline status</h3>
      </div>
      {rows.length === 0 && <p className="muted small">No recordings yet.</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Videos</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.status}>
                  <td>
                    <StatusBadge status={c.status} />
                  </td>
                  <td>{c.count}</td>
                  <td>{formatHours(c.duration_ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function JobsCard({ jobs }: { jobs: Record<string, number> }) {
  const order = ["queued", "running", "succeeded", "failed", "cancelled"];
  return (
    <section className="card">
      <div className="card-head">
        <h3>Jobs queue</h3>
      </div>
      <div className="kpi-row jobs-row">
        {order
          .filter((k) => k in jobs)
          .map((k) => (
            <div className="kpi-tile" key={k}>
              <span className="kpi-value">{jobs[k]}</span>
              <span className="kpi-label">{k}</span>
            </div>
          ))}
      </div>
    </section>
  );
}

export default function HomePage() {
  const dashQuery = useDashboard();
  const dash = dashQuery.data;
  const demo = useIsDemo();
  const mask = useSensitiveMask();

  return (
    <div className="home-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Home</h1>
          <p className="page-sub">All recordings, every user</p>
        </div>
        <div className="page-head-actions">
          <SensitiveToggle mask={mask} />
        </div>
      </div>

      {dashQuery.isPending && <p className="muted small">Loading…</p>}
      {dashQuery.isError && (
        <p className="error">Failed to load: {errorMessage(dashQuery.error)}</p>
      )}

      {dash && (
        <>
          <KpiRow dash={dash} />
          <div className="home-grid">
            <UserRankingCard users={dash.users} masked={mask.masked} />
            <ApprovedHoursChart users={dash.users} masked={mask.masked} />
            {!demo && <WeeklyChart dash={dash} />}
            <StatusBreakdownCard dash={dash} />
          </div>
          {dash.jobs && <JobsCard jobs={dash.jobs} />}
        </>
      )}
    </div>
  );
}
